"use client";

import React, { useEffect } from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { AlertCircle, AlertTriangle, Calendar as CalendarIcon, Clock, Info } from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import { AvailabilitySlotDTO } from "@/data/dto/theme.dto";
import { AsyncStatus, ASYNC_STATUS } from "@/hooks/use-booking-flow";
import { cn } from "@/lib/utils";

interface DateTimeStepProps {
    slotMode: "split" | "consecutive";
    slotCount: number;
    isConsecutiveRequired: boolean;
    isMultiDateEnabled: boolean;
    date1: Date | undefined;
    date2: Date | undefined;
    availableSlots1: AvailabilitySlotDTO[];
    availableSlots2: AvailabilitySlotDTO[];
    isLoading1: boolean;
    isLoading2: boolean;
    status1: AsyncStatus;
    status2: AsyncStatus;
    errorMsg: string | null;
    isDateBlocked: (date: Date) => boolean;
    onDate1Change: (date: Date | undefined) => void;
    onDate2Change: (date: Date | undefined) => void;
    onFetchAvailability1: () => void | Promise<void>;
    onFetchAvailability2: () => void | Promise<void>;
    onTimeSelect: (slot: AvailabilitySlotDTO, section: 1 | 2) => void;
    isTimeSelected: (time: string, section: 1 | 2) => boolean;
    getTimeRangeLabel: (section: 1 | 2) => string | null;
}

export const DateTimeStep: React.FC<DateTimeStepProps> = ({
    slotMode,
    slotCount,
    isConsecutiveRequired,
    isMultiDateEnabled,
    date1,
    date2,
    availableSlots1,
    availableSlots2,
    isLoading1,
    isLoading2,
    status1,
    status2,
    errorMsg,
    isDateBlocked,
    onDate1Change,
    onDate2Change,
    onFetchAvailability1,
    onFetchAvailability2,
    onTimeSelect,
    isTimeSelected,
    getTimeRangeLabel,
}) => {
    useEffect(() => {
        if (status1 !== ASYNC_STATUS.INITIAL) {
            return;
        }

        void onFetchAvailability1();
    }, [onFetchAvailability1, status1]);

    useEffect(() => {
        if (!isMultiDateEnabled || status2 !== ASYNC_STATUS.INITIAL) {
            return;
        }

        void onFetchAvailability2();
    }, [isMultiDateEnabled, onFetchAvailability2, status2]);

    const renderTimeGrid = (
        availableSlots: AvailabilitySlotDTO[],
        isLoading: boolean,
        status: AsyncStatus,
        section: 1 | 2
    ) => {
        const currentDate = section === 1 ? date1 : date2;

        if (currentDate && isDateBlocked(currentDate)) {
            return (
                <div className="flex flex-col items-center justify-center py-12 px-6 bg-destructive/5 rounded-2xl border border-dashed border-destructive/30 text-center space-y-3">
                    <AlertCircle className="w-8 h-8 text-destructive/60" />
                    <div className="space-y-1">
                        <p className="text-destructive font-bold text-sm tracking-tight">Booking is not available for this date</p>
                        <p className="text-muted-foreground text-xs leading-relaxed max-w-[200px] mx-auto">
                            Please contact <span className="font-bold text-foreground">Fauzi / Izzat</span> for help or further information.
                        </p>
                    </div>
                </div>
            );
        }

        if (status === ASYNC_STATUS.INITIAL || isLoading) {
            return (
                <div className="grid grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {[...Array(8)].map((_, index) => (
                        <div key={index} className="h-12 bg-muted/40 rounded-xl animate-pulse" />
                    ))}
                </div>
            );
        }

        if (availableSlots.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-10 bg-muted/10 rounded-2xl border border-dashed border-border/50">
                    <AlertTriangle className="w-6 h-6 text-muted-foreground/30 mb-2" />
                    <p className="text-muted-foreground text-[10px] uppercase tracking-widest font-medium">No slots available</p>
                </div>
            );
        }

        return (
            <div className="grid grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {availableSlots.map((slot) => {
                    const selected = isTimeSelected(slot.time, section);
                    const isError = selected && !slot.available;

                    return (
                        <button
                            key={slot.time}
                            onClick={() => onTimeSelect(slot, section)}
                            className={cn(
                                "relative p-3 rounded-2xl flex flex-col items-center justify-center gap-0.5 transition-all duration-300 border overflow-hidden min-h-[60px]",
                                selected
                                    ? slot.available
                                        ? "bg-primary border-primary text-primary-foreground shadow-lg shadow-primary/20"
                                        : "bg-destructive/10 border-destructive text-destructive shadow-sm"
                                    : !slot.available
                                        ? "bg-muted/5 border-border/40 text-muted-foreground/30"
                                        : "bg-card border-border text-foreground hover:border-primary/40 hover:text-primary hover:shadow-md"
                            )}
                        >
                            <span className="text-xs sm:text-sm font-bold tracking-tight">{slot.time}</span>
                            {!slot.available && (
                                <span
                                    className={cn(
                                        "text-[8px] uppercase tracking-widest font-black leading-none",
                                        isError ? "text-destructive" : "text-muted-foreground/40"
                                    )}
                                >
                                    Booked
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="space-y-12 max-w-6xl mx-auto">
            <div className="text-center space-y-4">
                <p className="text-muted-foreground max-w-md mx-auto text-sm leading-relaxed">
                    {slotMode === "consecutive"
                        ? slotCount === 1
                            ? "Choose an available date and start time for your session."
                            : `Choose a start time for your ${slotCount * 30}-minute session. We'll reserve ${slotCount} consecutive slots for you.`
                        : slotCount === 3
                        ? "Choose a start time for your 60-minute session. We'll reserve three consecutive slots for you."
                        : slotCount === 2
                            ? "Please select two separate dates and times for your 40-minute package sessions."
                            : "Choose a time that works best for your schedule."}
                </p>
            </div>

            <div
                className={cn(
                    "grid gap-16 items-start",
                    isMultiDateEnabled ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1 lg:grid-cols-2"
                )}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:contents">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex flex-col"
                    >
                        <div className="flex items-center gap-3 mb-6 text-foreground font-medium uppercase tracking-[0.12em] text-[10px]">
                            <CalendarIcon className="w-4 h-4 text-primary" />
                            <span>{isMultiDateEnabled ? "Select Date (Session 1)" : "Select Date"}</span>
                        </div>
                        <Calendar
                            mode="single"
                            selected={date1}
                            onSelect={onDate1Change}
                            className="rounded-md mx-auto"
                            disabled={(date) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                return date < today || isDateBlocked(date);
                            }}
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-6"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 text-foreground font-medium uppercase tracking-[0.12em] text-[10px]">
                                <Clock className="w-4 h-4 text-primary" />
                                <span>{isMultiDateEnabled ? "Time (Session 1)" : "Select Time"}</span>
                            </div>
                            {date1 && (
                                <div className="flex flex-col items-end gap-1">
                                    <span className="text-[10px] font-semibold text-primary/70 uppercase tracking-widest px-3 py-1 bg-primary/5 rounded-full">
                                        {format(date1, "MMM d, yyyy")}
                                    </span>
                                    {getTimeRangeLabel(1) && (
                                        <span className="text-[10px] font-bold text-foreground/60 uppercase tracking-widest mr-2">
                                            {getTimeRangeLabel(1)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {renderTimeGrid(availableSlots1, isLoading1, status1, 1)}
                    </motion.div>
                </div>

                {isMultiDateEnabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:contents border-t xl:border-t-0 xl:border-l border-border/50 pt-16 xl:pt-0 xl:pl-16">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="flex flex-col"
                        >
                            <div className="flex items-center gap-3 mb-6 text-foreground font-medium uppercase tracking-[0.12em] text-[10px]">
                                <CalendarIcon className="w-4 h-4 text-primary" />
                                <span>Select Date (Session 2)</span>
                            </div>
                            <Calendar
                                mode="single"
                                selected={date2}
                                onSelect={onDate2Change}
                                className="rounded-md mx-auto"
                                disabled={(date) => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    return date < today || isDateBlocked(date);
                                }}
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="space-y-6"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 text-foreground font-medium uppercase tracking-[0.12em] text-[10px]">
                                    <Clock className="w-4 h-4 text-primary" />
                                    <span>Time (Session 2)</span>
                                </div>
                                {date2 && (
                                    <div className="flex flex-col items-end gap-1">
                                    <span className="text-[10px] font-semibold text-primary/70 uppercase tracking-widest px-3 py-1 bg-primary/5 rounded-full">
                                            {format(date2, "MMM d, yyyy")}
                                        </span>
                                        {getTimeRangeLabel(2) && (
                                            <span className="text-[10px] font-bold text-foreground/60 uppercase tracking-widest mr-2">
                                                {getTimeRangeLabel(2)}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            {renderTimeGrid(availableSlots2, isLoading2, status2, 2)}
                        </motion.div>
                    </div>
                )}
            </div>

            {errorMsg && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-xl mx-auto p-4 bg-destructive/10 border border-destructive/20 rounded-2xl flex items-center gap-3 text-destructive text-sm"
                >
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <p>{errorMsg}</p>
                </motion.div>
            )}

            {!isMultiDateEnabled && isConsecutiveRequired && (
                <div className="max-w-2xl mx-auto p-6 bg-primary/3 rounded-[2rem] border border-primary/10 flex gap-4 text-primary/70 text-xs leading-relaxed">
                    <Info className="w-5 h-5 shrink-0 opacity-60" />
                    <div>
                        <p className="font-semibold mb-1">Consecutive Slots Selection</p>
                        <p>
                            {slotMode === "consecutive"
                                ? `This booking reserves ${slotCount} back-to-back 30-minute slot${slotCount === 1 ? "" : "s"}. Selecting a start time will automatically reserve the remaining consecutive slots when required.`
                                : "This premium 60-minute package reserves three back-to-back 20-minute slots. Selecting a slot will automatically attempt to book the two subsequent times."}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
